// @flow
import { startRequest, finishRequest } from "@store/utils";
import type { DataState, FileSummaryState } from "./types";
import {
  GetDataFiles,
  GetDataFilesSuccess,
  GetDataFilesError,
  GetDataFileArchiveSummary,
  GetDataFileArchiveSummarySuccess,
  GetDataFileArchiveSummaryError,
  OpenFileChangesSummaryModal,
  CloseFileChangesSummaryModal,
  GetFileChangesDetails,
  GetFileChangesDetailsSuccess,
  GetFileChangesDetailsError,
  OpenFileChangesDetailsModal,
  CloseFileChangesDetailsModal,
  ToggleLoadDataModal,
  CreateDataFile,
  CreateDataFileError,
  CreateDataFileSuccess,
  GetValidatingStatusSuccess,
  SetDataFileStatus,
  GetActiveHierarchy,
  GetActiveHierarchyError,
  GetActiveHierarchySuccess,
  GetExportHistory,
  GetExportHistorySuccess,
  GetExportHistoryError,
  ExportTransferFileHistory,
  ExportTransferFileHistorySuccess,
  ExportFileLogHistory,
  ExportFileLogHistorySuccess,
  GetTransferHistory,
  GetTransferHistorySuccess,
  GetTransferHistoryError,
  ToggleResumeDataModal,
  GetDataExceptionTypesSuccess,
  GetDataExceptionsByType,
  GetDataExceptionsByTypeSuccess,
  SetDataExceptionTypeConfirmSuccess,
  DeleteDataFile,
  DeleteDataFileSuccess,
  DeleteDataFileError,
  ApproveDataExceptions,
  ApproveDataExceptionsSuccess,
  ApproveDataExceptionsError,
  UpdateDraftExceptions,
  OpenUnsavedChangesModal,
  CloseUnsavedChangesModal,
  DiscardUnsavedChanges,
  GetDataDashboardOverview,
  GetDataDashboardOverviewSuccess,
  GetDataQualityIssues,
  GetDataQualityIssuesSuccess,
  ExportDataQualityIssues,
  ExportDataQualityIssuesSuccess,
  DownloadTransferHistoryFile,
  DownloadTransferHistoryFileSuccess,
  DownloadTransferHistoryFileError,
  DownloadDataSipFile,
  DownloadDataSipFileSuccess,
  DownloadDataSipFileError,
  ExportSipFileExceptions,
  ExportSipFileExceptionsSuccess,
  ExportSipFileExceptionsError,
  DownloadSipFileChangesDetails,
  DownloadSipFileChangesDetailsSuccess,
  DownloadSipFileChangesDetailsError,
  ExportDataFile,
  ExportDataFileSuccess,
  ExportDataFileError,
  GetDataFileArchive,
  GetDataFileArchiveSuccess,
  GetDataFileArchiveError,
  DeleteTransferHistoryFile,
  DeleteTransferHistoryFileSuccess,
  DeleteTransferHistoryFileError,
  ToggleReviewDataChangesModal,
  GetReviewDataChanges,
  GetReviewDataChangesSuccess,
  GetReviewDataChangesError,
  ResetReviewData,
  GetSipExportHeadcount,
  GetSipExportHeadcountSuccess,
  GetSipExportHeadcountError,
  CreateOfflineExport,
  CreateOfflineExportSuccess,
  CreateOfflineExportError,
  DownloadOfflineExportFile,
  DownloadOfflineExportFileSuccess,
  DownloadOfflineExportFileError,
} from "@modules/data/constants/actions";

import { cloneObject } from "@utils/object";

export const initialState: DataState = {
  requestsInProgress: 0,
  dataFilesRequestsInProgress: 0,
  activeHierarchy: null,
  exportHistory: {
    content: [],
    pagination: {},
    totalElement: 0,
  },
  transferHistory: {
    content: [],
    pagination: { pageNumber: 0, offset: 0, pageSize: 0, sort: { orders: [] } },
    totalElements: 0,
  },
  sipExportHeadcount: null,
  files: null,
  dataFileArchive: {
    files: {
      error: null,
      list: null,
      pagination: null,
    },
    fileSummary: {
      error: null,
      id: null,
      summary: null,
    },
  },
  dataFileStatus: "NO_FILE",
  fileChangesSummary: null,
  fileChangesDetails: null,
  modals: {
    fileChangesSummary: {
      visible: false,
    },
    fileChangesDetails: {
      visible: false,
    },
    loadNewData: {
      visible: false,
      formData: null,
    },
    resumeData: {
      visible: false,
    },
    unsavedChanges: {
      visible: false,
    },
    reviewChanges: {
      visible: false,
    },
  },
  error: null,
  exceptions: [],
  draftExceptions: [],
  dataMeasures: [],
  dataQualityIssues: {
    content: [],
    pagination: {
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
    },
    totalElements: 0,
  },
  reviewChanges: [],
};

export const setLoading = (state: DataState) => () => {
  return {
    ...startRequest(state),
  };
};

export const setLoaded = (state: DataState) => () => {
  return {
    ...finishRequest(state),
  };
};

const onError = (state: FileSummaryState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  error: payload,
});

export const onGetDataFiles = (state: DataState) => () => ({
  ...state,
  dataFilesRequestsInProgress: state.dataFilesRequestsInProgress + 1,
});

export const onGetDataFilesSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    dataFilesRequestsInProgress: state.dataFilesRequestsInProgress - 1,
    error: null,
    dataFileStatus: payload ? payload.status : "NO_FILE",
    files: payload || null,
  };
};

export const onGetFilesError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    dataFilesRequestsInProgress: state.dataFilesRequestsInProgress - 1,
    error: payload,
  };
};

export const onGetDataFileArchiveSummarySuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    fileChangesSummary: payload,
  };
};

export const onGetFileChangesDetailsSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    fileChangesDetails: payload,
  };
};

export const onGetActiveHierarchySuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    activeHierarchy: payload,
  };
};

export const onGetActiveHierarchyError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetExportHistorySuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    exportHistory: payload,
  };
};
export const onExportTransferFileHistorySuccess = (state: DataState) => () => {
  return {
    ...finishRequest(state),
  };
};

export const onExportFileLogHistorySuccess = (state: DataState) => () => {
  return {
    ...finishRequest(state),
  };
};

export const onGetExportHistoryError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    exportHistory: payload,
  };
};
export const onGetTransferHistorySuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    transferHistory: payload,
  };
};
export const onGetTransferHistoryError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    transferHistory: payload,
  };
};

export const onCreateDataFileSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    error: null,
    dataFileStatus: payload.status,
    files: payload,
    modals: {
      ...state.modals,
      loadNewData: {
        visible: false,
        formData: null,
      },
    },
  };
};

export const onCreateDataFileError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload.data,
    dataFileStatus: "NO_FILE",
    modals: {
      ...state.modals,
      loadNewData: {
        visible: false,
        formData: null,
      },
    },
  };
};

export const onOpenFileChangesSummaryModal = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      fileChangesSummary: {
        ...state.modals.fileChangesSummary,
        visible: true,
      },
    },
  };
};

export const onCloseFileChangesSummaryModal = (state: DataState) => () => {
  return {
    ...state,
    modals: {
      ...state.modals,
      fileChangesSummary: {
        ...state.modals.fileChangesSummary,
        visible: false,
      },
    },
  };
};
export const onCloseFileChangesDetailsModal = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      fileChangesDetails: {
        ...state.modals.fileChangesDetails,
        visible: false,
      },
    },
  };
};
export const onOpenFileChangesDetailsModal = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      fileChangesDetails: {
        ...state.modals.fileChangesDetails,
        visible: true,
      },
    },
  };
};

export const onToggleLoadDataModal = (state: DataState) => () => {
  state.modals.loadNewData.visible = !state.modals.loadNewData.visible;
  state.modals.loadNewData.formData = null;
  return state;
};

export const onCreateDataFile = (state: DataState) => (formData: Object) => {
  return {
    ...state,
    dataFileStatus: "LOADING",
    modals: {
      ...state.modals,
      loadNewData: { ...state.modals.loadNewData, formData: formData },
    },
  };
};

export const onToggleResumeDataModal = (state: DataState) => () => {
  return {
    ...state,
    modals: {
      ...state.modals,
      resumeData: {
        ...state.modals.resumeData,
        visible: !state.modals.resumeData.visible,
      },
    },
  };
};

export const onGetDataExceptionTypesSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    exceptions: payload,
  };
};

export const onGetDataExceptionsByTypeSuccess = (state: DataState) => ({ payload }: Object) => {
  const exceptions = state.exceptions.map(type =>
    type.id === payload.typeId ? { ...type, list: payload } : type,
  );

  return {
    ...finishRequest(state),
    error: null,
    exceptions,
  };
};

export const onSetDataExceptionTypeConfirmSuccess = (state: DataState) => ({ payload }: Object) => {
  const exceptions = state.exceptions.map(item => {
    if (item.id === payload.id) {
      return {
        ...payload,
        list: {
          ...item.list,
          content: item.list.content.map(c => {
            const match = payload.exceptions.find(m => m.id === c.id);
            return {
              ...c,
              status: match ? match.status : c.status,
            };
          }),
        },
      };
    }

    return item;
  });

  return {
    ...finishRequest(state),
    error: null,
    exceptions,
    draftExceptions: [],
  };
};

export const onDeleteDataFileSuccess = (state: DataState) => () => {
  return {
    ...finishRequest(state),
    error: null,
    exceptions: [],
    modals: {
      ...state.modals,
      resumeData: {
        ...state.modals.resumeData,
        visible: false,
      },
    },
    files: {
      ...state.files,
      content: [],
      pagination: {
        sort: { orders: [] },
        pageNumber: 0,
        pageSize: 0,
        offset: 0,
      },
    },
  };
};

export const onApproveDataExceptionsSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    exceptions: [],
    files: payload,
    dataFileStatus: payload.status,
  };
};

export const onApproveDataExceptionsError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onUpdateDraftExceptions = (state: DataState) => ({ payload }: Object) => ({
  ...state,
  draftExceptions: payload.draftExceptions,
});

export const onOpenUnsavedChangesModal = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      unsavedChanges: {
        ...state.modals.unsavedChanges,
        visible: true,
      },
    },
  };
};

export const onToggleReviewDataChangesModal = (state: DataState) => () => {
  return {
    ...state,
    modals: {
      ...state.modals,
      reviewChanges: {
        visible: !state.modals.reviewChanges.visible,
      },
    },
  };
};

export const onCloseUnsavedChangesModal = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      unsavedChanges: {
        ...state.modals.unsavedChanges,
        visible: false,
      },
    },
  };
};

export const onDiscardUnsavedChanges = (state: DataState) => () => ({
  ...state,
  modals: {
    ...state.modals,
    resumeData: {
      visible: false,
    },
    unsavedChanges: {
      visible: false,
    },
  },
  draftExceptions: [],
  exceptions: [],
});

export const onGetValidatingStatus = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    filestatusQueryInProgress: true,
    dataFileStatus: payload ? payload.status : "COMPLETE",
    error: null,
    files: payload,
  };
};
export const onGetValidatingStatusSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    filestatusQueryInProgress: false,
    dataFileStatus: payload ? payload.status : "COMPLETE",
    error: null,
    files: typeof payload == "object" ? payload : state.files,
  };
};

export const onSetDataFileStatus = (state: DataState) => ({ payload }: Object) => {
  return {
    ...state,
    dataFileStatus: payload,
  };
};

export const onGetDataDashboardOverviewSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    dataMeasures: payload,
  };
};
export const onGetDataQualityIssuesSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    dataQualityIssues: payload,
  };
};

export const onGetSipExportHeadcountSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    sipExportHeadcount: payload,
  };
};

export const onGetSipExportHeadcountError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    sipExportHeadcount: null,
    error: payload,
  };
};

export const onCreateOfflineExportError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onDownloadOfflineExportFileError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onDownloadTransferHistoryFileSuccess = (state: DataState) => () => {
  return {
    ...finishRequest(state),
  };
};

export const onDownloadTransferHistoryFileError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onDownloadDataSipFileError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onExportSipFileExceptionsError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onDownloadSipFileChangesDetailsError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onExportDataFileError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

const onGetDataFileArchiveSuccess = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    dataFileArchive: {
      ...state.dataFileArchive,
      files: {
        ...state.dataFileArchive.files,
        error: null,
        list: payload.files,
        pagination: payload.pagination,
      },
    },
  };
};

export const onGetDataFileArchiveError = (state: DataState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    dataFileArchive: {
      ...state.dataFileArchive,
      files: {
        ...state.dataFileArchive.files,
        error: payload.error,
      },
    },
  };
};

const onGetDataFileArchiveSummaryError = (state: FileSummaryState) => ({ payload }: Object) => {
  return {
    ...state,
    error: payload,
  };
};

const onGetReviewDataChangesSuccess = (state: FileSummaryState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  reviewChanges: payload,
});

const onResetReviewData = (state: FileSummaryState) => () => ({
  ...finishRequest(state),
  reviewChanges: [],
});

export default (state: DataState = initialState, action: *): DataState => {
  const act = {
    [GetDataFiles]: onGetDataFiles,
    [GetDataFilesSuccess]: onGetDataFilesSuccess,
    [GetDataFilesError]: onGetFilesError,
    [GetDataFileArchiveSummary]: setLoading,
    [GetDataFileArchiveSummarySuccess]: onGetDataFileArchiveSummarySuccess,
    [GetDataFileArchiveSummaryError]: setLoaded,
    [OpenFileChangesSummaryModal]: onOpenFileChangesSummaryModal,
    [CloseFileChangesSummaryModal]: onCloseFileChangesSummaryModal,
    [GetFileChangesDetails]: setLoading,
    [GetFileChangesDetailsSuccess]: onGetFileChangesDetailsSuccess,
    [GetFileChangesDetailsError]: setLoading,
    [OpenFileChangesDetailsModal]: onOpenFileChangesDetailsModal,
    [CloseFileChangesDetailsModal]: onCloseFileChangesDetailsModal,
    [ToggleLoadDataModal]: onToggleLoadDataModal,
    [CreateDataFile]: onCreateDataFile,
    [ExportDataFile]: setLoading,
    [ExportDataFileSuccess]: setLoaded,
    [ExportDataFileError]: onExportDataFileError,
    [GetValidatingStatusSuccess]: onGetValidatingStatusSuccess,
    [SetDataFileStatus]: onSetDataFileStatus,
    [CreateDataFileSuccess]: onCreateDataFileSuccess,
    [CreateDataFileError]: onCreateDataFileError,
    [GetActiveHierarchy]: setLoading,
    [GetActiveHierarchySuccess]: onGetActiveHierarchySuccess,
    [GetActiveHierarchyError]: onGetActiveHierarchyError,
    [GetExportHistory]: setLoading,
    [GetExportHistorySuccess]: onGetExportHistorySuccess,
    [GetExportHistoryError]: onGetExportHistoryError,
    [ExportTransferFileHistory]: setLoading,
    [ExportTransferFileHistorySuccess]: onExportTransferFileHistorySuccess,
    [DownloadTransferHistoryFile]: setLoading,
    [DownloadTransferHistoryFileSuccess]: onDownloadTransferHistoryFileSuccess,
    [DownloadTransferHistoryFileError]: onDownloadTransferHistoryFileError,
    [ExportFileLogHistory]: setLoading,
    [ExportFileLogHistorySuccess]: onExportFileLogHistorySuccess,
    [GetTransferHistory]: setLoading,
    [GetTransferHistorySuccess]: onGetTransferHistorySuccess,
    [GetTransferHistoryError]: onGetTransferHistoryError,
    [DeleteTransferHistoryFile]: setLoading,
    [DeleteTransferHistoryFileSuccess]: setLoaded,
    [DeleteTransferHistoryFileError]: setLoaded,
    [ToggleResumeDataModal]: onToggleResumeDataModal,
    [GetDataExceptionTypesSuccess]: onGetDataExceptionTypesSuccess,
    [GetDataExceptionsByType]: setLoading,
    [GetDataExceptionsByTypeSuccess]: onGetDataExceptionsByTypeSuccess,
    [SetDataExceptionTypeConfirmSuccess]: onSetDataExceptionTypeConfirmSuccess,
    [DeleteDataFile]: setLoading,
    [DeleteDataFileSuccess]: onDeleteDataFileSuccess,
    [DeleteDataFileError]: setLoading,
    [ApproveDataExceptions]: setLoading,
    [ApproveDataExceptionsSuccess]: onApproveDataExceptionsSuccess,
    [ApproveDataExceptionsError]: onApproveDataExceptionsError,
    [UpdateDraftExceptions]: onUpdateDraftExceptions,
    [OpenUnsavedChangesModal]: onOpenUnsavedChangesModal,
    [CloseUnsavedChangesModal]: onCloseUnsavedChangesModal,
    [DiscardUnsavedChanges]: onDiscardUnsavedChanges,
    [GetDataDashboardOverview]: setLoading,
    [GetDataDashboardOverviewSuccess]: onGetDataDashboardOverviewSuccess,
    [GetDataQualityIssues]: setLoading,
    [GetDataQualityIssuesSuccess]: onGetDataQualityIssuesSuccess,
    [ExportDataQualityIssues]: setLoading,
    [ExportDataQualityIssuesSuccess]: setLoaded,
    [DownloadDataSipFile]: setLoading,
    [DownloadDataSipFileSuccess]: setLoaded,
    [DownloadDataSipFileError]: onDownloadDataSipFileError,
    [ExportSipFileExceptions]: setLoading,
    [ExportSipFileExceptionsSuccess]: setLoaded,
    [ExportSipFileExceptionsError]: onExportSipFileExceptionsError,
    [DownloadSipFileChangesDetails]: setLoading,
    [DownloadSipFileChangesDetailsSuccess]: setLoaded,
    [DownloadSipFileChangesDetailsError]: onDownloadSipFileChangesDetailsError,
    [ToggleReviewDataChangesModal]: onToggleReviewDataChangesModal,
    [GetReviewDataChanges]: setLoading,
    [GetReviewDataChangesSuccess]: onGetReviewDataChangesSuccess,
    [GetReviewDataChangesError]: onError,
    [ResetReviewData]: onResetReviewData,
    [GetSipExportHeadcount]: setLoading,
    [GetSipExportHeadcountSuccess]: onGetSipExportHeadcountSuccess,
    [GetSipExportHeadcountError]: onGetSipExportHeadcountError,
    [CreateOfflineExport]: setLoading,
    [CreateOfflineExportSuccess]: setLoaded,
    [CreateOfflineExportError]: onCreateOfflineExportError,
    [DownloadOfflineExportFile]: setLoading,
    [DownloadOfflineExportFileSuccess]: setLoaded,
    [DownloadOfflineExportFileError]: onDownloadOfflineExportFileError,

    [GetDataFileArchive]: setLoading,
    [GetDataFileArchiveSuccess]: onGetDataFileArchiveSuccess,
    [GetDataFileArchiveError]: onGetDataFileArchiveError,

    [GetDataFileArchiveSummary]: setLoading,
    [GetDataFileArchiveSummaryError]: onGetDataFileArchiveSummaryError,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
